/**
 * Utilidades para estandarizar fechas en formato dd/mm/aaaa y dd/mm/aaaa hh:mm PM.
 */

/**
 * Formatea una fecha o cadena a dd/mm/aaaa.
 * Ej: '2026-09-01' -> '01/09/2026'
 *     '2026-09-01T14:30:00Z' -> '01/09/2026'
 *     '01/09/2026' -> '01/09/2026'
 */
export function formatDate(val) {
  if (!val) return "";
  const s = String(val).trim();
  if (!s || s === "null" || s === "undefined" || s === "None") return "";

  // Si ya viene en dd/mm/aaaa
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(s)) {
    return s;
  }

  // Si viene en YYYY-MM-DD o YYYY/MM/DD (con o sin tiempo adicional)
  const matchYMD = s.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})/);
  if (matchYMD) {
    const [, y, m, d] = matchYMD;
    return `${d.padStart(2, "0")}/${m.padStart(2, "0")}/${y}`;
  }

  // Si viene en DD-MM-YYYY
  const matchDMY = s.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})/);
  if (matchDMY) {
    const [, d, m, y] = matchDMY;
    return `${d.padStart(2, "0")}/${m.padStart(2, "0")}/${y}`;
  }

  try {
    const date = new Date(s);
    if (!isNaN(date.getTime())) {
      const d = String(date.getDate()).padStart(2, "0");
      const m = String(date.getMonth() + 1).padStart(2, "0");
      const y = date.getFullYear();
      return `${d}/${m}/${y}`;
    }
  } catch (e) {}

  return s;
}

/**
 * Formatea una fecha o marca temporal a dd/mm/aaaa hh:mm AM/PM.
 * Ej: '2026-09-01T15:20:00Z' -> '01/09/2026 03:20 PM'
 *     '03/04/2025 15:20' -> '03/04/2025 03:20 PM'
 */
export function formatDateTime(val) {
  if (!val) return "";
  const s = String(val).trim();
  if (!s || s === "null" || s === "undefined" || s === "None") return "";

  // Si ya tiene el formato DD/MM/AAAA hh:mm AM/PM
  const matchAlready = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})\s+(\d{1,2}):(\d{2})(?::\d{2})?\s*(AM|PM|am|pm)?$/i);
  if (matchAlready) {
    const [, d, mo, y, h, mi, ampm] = matchAlready;
    let hInt = parseInt(h, 10);
    let ampmStr = ampm ? ampm.toUpperCase() : (hInt >= 12 ? "PM" : "AM");
    if (!ampm) {
      if (hInt > 12) hInt -= 12;
      else if (hInt === 0) hInt = 12;
    }
    return `${d.padStart(2, "0")}/${mo.padStart(2, "0")}/${y} ${String(hInt).padStart(2, "0")}:${mi} ${ampmStr}`;
  }

  // Si tiene Z o offset de zona horaria UTC
  if (s.endsWith("Z") || /[+-]\d{2}:\d{2}$/.test(s)) {
    try {
      const date = new Date(s);
      if (!isNaN(date.getTime())) {
        const d = String(date.getDate()).padStart(2, "0");
        const mo = String(date.getMonth() + 1).padStart(2, "0");
        const y = date.getFullYear();
        let hours = date.getHours();
        const minutes = String(date.getMinutes()).padStart(2, "0");
        const ampm = hours >= 12 ? "PM" : "AM";
        hours = hours % 12;
        hours = hours ? hours : 12;
        const hStr = String(hours).padStart(2, "0");
        return `${d}/${mo}/${y} ${hStr}:${minutes} ${ampm}`;
      }
    } catch (e) {}
  }

  // Si viene en formato ISO YYYY-MM-DDTHH:MM:SS
  const matchIso = s.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})[T\s](\d{1,2}):(\d{2})(?::(\d{2}))?/);
  if (matchIso) {
    const [, y, mo, d, h, mi] = matchIso;
    let hInt = parseInt(h, 10);
    const ampmStr = hInt >= 12 ? "PM" : "AM";
    if (hInt > 12) hInt -= 12;
    else if (hInt === 0) hInt = 12;
    return `${d.padStart(2, "0")}/${mo.padStart(2, "0")}/${y} ${String(hInt).padStart(2, "0")}:${mi} ${ampmStr}`;
  }

  try {
    const date = new Date(s);
    if (!isNaN(date.getTime())) {
      const d = String(date.getDate()).padStart(2, "0");
      const mo = String(date.getMonth() + 1).padStart(2, "0");
      const y = date.getFullYear();
      let hours = date.getHours();
      const minutes = String(date.getMinutes()).padStart(2, "0");
      const ampm = hours >= 12 ? "PM" : "AM";
      hours = hours % 12;
      hours = hours ? hours : 12;
      const hStr = String(hours).padStart(2, "0");
      return `${d}/${mo}/${y} ${hStr}:${minutes} ${ampm}`;
    }
  } catch (e) {}

  return s;
}

/**
 * Convierte cualquier fecha dd/mm/aaaa o YYYY-MM-DD a YYYY-MM-DD (estándar para APIs).
 */
export function parseToISO(val) {
  if (!val) return "";
  const s = String(val).trim();
  if (!s || s === "null" || s === "undefined") return "";

  const matchDMY = s.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})/);
  if (matchDMY) {
    const [, d, m, y] = matchDMY;
    return `${y}-${m.padStart(2, "0")}-${d.padStart(2, "0")}`;
  }

  const matchYMD = s.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})/);
  if (matchYMD) {
    const [, y, m, d] = matchYMD;
    return `${y}-${m.padStart(2, "0")}-${d.padStart(2, "0")}`;
  }

  return s;
}

/**
 * Retorna la fecha de hoy en formato dd/mm/aaaa.
 */
export function getTodayFormatted() {
  const now = new Date();
  const d = String(now.getDate()).padStart(2, "0");
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const y = now.getFullYear();
  return `${d}/${m}/${y}`;
}
